import type {
  UserData,
  EncryptedData,
  UserInviteResponse,
  UserInvitesFilters,
  UserLogsFilters,
  UserLogEntry,
  InviteAnswer,
  UserFromInviteData,
  ApiKeyResponse,
  UserInviteRequest,
  PaginatedResponse,
  EulaResponse,
  OrganizationUserResponse,
  OrganizationUsersFilters,
  InviteUpdateRequest,
} from "~/types/global";
import { REPORTS_CLIENT } from "~/services/clients/reports.client";
import { ReadUserOutputDTO } from "root/domain/http/dto/user/read.user.output";
import { parseToTimezoneString } from "~/helpers";

/**
 * Fetches the current user data.
 */
export const fetchUserData = async (): Promise<UserData> => {
  try {
    const response = await REPORTS_CLIENT.get<UserData>(`/auth/user`);
    // evita falso positivo pois requisicao retorna html
    // tornando a resposta um objeto UserData positivo
    // quebrando validacoes de existencia
    const userData = ReadUserOutputDTO.process(response.data);
    console.log("[/auth/user] response.data: ", response.data);
    return userData;
  } catch (error) {
    console.error("Error fetching user data:", error);
    throw error;
  }
};

export const postVerifier = async (verifier: EncryptedData) => {
  try {
    const response = await REPORTS_CLIENT.post<EncryptedData>(`/verifier`, {
      verifier,
    });
    const data = response.data;
    return data;
  } catch (error) {
    console.error("Error posting verifier:", error);
    throw error;
  }
};

export const postClearMustChangeSecretKey = async (verifier: EncryptedData) => {
  try {
    const response = await REPORTS_CLIENT.post(`/clear-must-change-secret-key`, {
      verifier,
    });
    return response.data;
  } catch (error) {
    console.error("Error clearing must_change_secret_key:", error);
    throw error;
  }
};

export const postChangeUserPassword = async (verifier: EncryptedData) => {
  try {
    const response = await REPORTS_CLIENT.post(`/change-user-password`, {
      verifier,
    });
    return response.data;
  } catch (error) {
    console.error("Error changing user password:", error);
    throw error;
  }
};

export interface UserWrappedDeksPage {
  items: Array<{ id: string; user_wrapped_dek: string; user_dek_iv: string; item_type: string }>;
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export const getUserWrappedDeks = async (page = 1, pageSize = 100): Promise<UserWrappedDeksPage> => {
  try {
    const response = await REPORTS_CLIENT.get(`/user-wrapped-deks`, {
      params: { page, page_size: pageSize },
    });
    return response.data as UserWrappedDeksPage;
  } catch (error) {
    console.error("Error fetching user wrapped DEKs:", error);
    throw error;
  }
};

export const postUserRewrapDeks = async (items: Array<{
  id: string;
  new_user_wrapped_dek: string;
  new_user_dek_iv: string;
  item_type: string;
  hmacs?: Record<string, string[]>;
}>) => {
  try {
    const response = await REPORTS_CLIENT.post(`/user-rewrap-deks`, { items });
    return response.data;
  } catch (error) {
    console.error("Error re-wrapping DEKs:", error);
    throw error;
  }
};

export const postApiKey = async (apiKey: string): Promise<ApiKeyResponse> => {
  try {
    if (!apiKey?.trim()) {
      throw new Error("API key is required");
    }

    const response = await REPORTS_CLIENT.post<ApiKeyResponse>(`/set_apikey`, {
      apiKey: apiKey.trim(),
    });
    return response.data;
  } catch (error) {
    console.error("Error setting API key:", error);
    throw error;
  }
};

export const postUserInvite = async ({
  email_invited,
  credits_sent,
  report_types,
  type_invite,
  type_license,
}: UserInviteRequest): Promise<UserInviteResponse> => {
  console.log("[/create_invite] email_invited: ", email_invited);
  console.log("[/create_invite] credits_sent: ", credits_sent);
  console.log("[/create_invite] report_types: ", report_types);
  console.log("[/create_invite] type_invite: ", type_invite);
  try {
    if (!email_invited?.trim()) {
      throw new Error("Email is required");
    }
    if (!credits_sent?.trim()) {
      throw new Error("Credits amount is required");
    }
    if (!type_invite?.trim()) {
      throw new Error("Invite type is required");
    }
    if (!report_types || report_types.length === 0) {
      throw new Error("At least one report type is required");
    }

    const payload = {
      email_invited: email_invited.trim(),
      credits_sent: credits_sent.trim(),
      report_types: report_types.filter((type) => type?.trim()),
      type_invite: type_invite.trim(),
      type_license: type_license ?? "recorrente",
    };

    console.log("[/create_invite] payload: ", payload);
    const response = await REPORTS_CLIENT.post<UserInviteResponse>(
      `/create_invite`,
      payload
    );
    console.log("[/create_invite] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error sending invite:", error);
    throw error;
  }
};

export const getOrganizationAvailableCredits = async (): Promise<{
  available_credits: number | null;
}> => {
  const response = await REPORTS_CLIENT.get<{ available_credits: number | null }>(
    `/get_organization_available_credits`
  );
  return response.data;
};

export const getUserInvites = async (
  filters: UserInvitesFilters = {}
): Promise<PaginatedResponse<UserInviteResponse>> => {
  const {
    status_invite = "",
    type_invite = "",
    specific_date = "",
    order = "desc",
    column_order = "sent_at",
    limit = 10,
    page = 1,
    search_email = "",
  } = filters;
  try {
    console.log("[/get_organization_invite] filters: ", filters);
    const queryParams = new URLSearchParams();

    if (status_invite?.trim()) {
      queryParams.append("status_invite", status_invite.trim());
    }
    if (type_invite?.trim()) {
      queryParams.append("type_invite", type_invite.trim());
    }
    if (specific_date?.trim()) {
      queryParams.append("specific_date", parseToTimezoneString(specific_date));
    }
    if (search_email?.trim()) {
      queryParams.append("search_filter", search_email.trim());
    }

    queryParams.append("order", order || "desc");
    queryParams.append("column_order", column_order || "sent_at");
    queryParams.append("limit", limit.toString());
    queryParams.append("page", page.toString());

    const response = await REPORTS_CLIENT.get<
      PaginatedResponse<UserInviteResponse>
    >(`/get_organization_invite?${queryParams.toString()}`);
    console.log("[/get_organization_invite] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error getting invites:", error);
    throw error;
  }
};

export const getUserLogs = async (
  filters: UserLogsFilters = {}
): Promise<PaginatedResponse<UserLogEntry>> => {
  const {
    is_user = true,
    column_order = "created_at",
    order = "desc",
    report_status,
    report_type,
    search_filter,
    limit = 10,
    page = 1,
    created_start_at,
    created_end_at,
  } = filters;
  try {
    console.log("[/get-logs] filters: ", filters);
    const queryParams = new URLSearchParams();

    queryParams.append("is_user", is_user.toString());
    queryParams.append("order", order || "desc");
    queryParams.append("column_order", column_order || "created_at");
    queryParams.append("limit", limit.toString());
    queryParams.append("page", page.toString());

    if (report_status?.trim()) {
      queryParams.append("report_status", report_status.trim());
    }
    if (report_type?.trim()) {
      queryParams.append("report_type", report_type.trim());
    }
    if (search_filter?.trim()) {
      queryParams.append("search_filter", search_filter.trim());
    }
    if (created_start_at?.trim()) {
      queryParams.append("created_start_at", parseToTimezoneString(created_start_at));
    }
    if (created_end_at?.trim()) {
      queryParams.append("created_end_at", parseToTimezoneString(created_end_at));
    }

    const response = await REPORTS_CLIENT.get<PaginatedResponse<UserLogEntry>>(
      `/get-logs?${queryParams.toString()}`
    );
    console.log("[/get-logs] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error getting user logs:", error);
    throw error;
  }
};

export const getUserFromInvite = async (
  invite_id: string
): Promise<UserFromInviteData> => {
  try {
    console.log("[/get_user_data_from_invite] invite_id: ", invite_id);
    if (!invite_id?.trim()) {
      throw new Error("Invite ID is required");
    }

    const response = await REPORTS_CLIENT.get<UserFromInviteData>(
      `/get_user_data_from_invite/${invite_id.trim()}`
    );
    console.log("[/get_user_data_from_invite] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error getting user from invite:", error);
    throw error;
  }
};

export const editInvitedUser = async (
  user_id: string,
  report_types: any,
  role: string,
  credits_monthly: number
) => {
  try {
    console.log("[/update_invited_user] user_id: ", user_id);
    console.log("[/update_invited_user] report_types: ", report_types);
    console.log("[/update_invited_user] role: ", role);
    console.log("[/update_invited_user] credits_monthly: ", credits_monthly);
    const response = await REPORTS_CLIENT.put(
      `/update_invited_user/${user_id}`,
      { report_types, role, credits_monthly }
    );
    return response.data;
  } catch (error) {
    console.error("Error editing invited user:", error);
    throw error;
  }
};

export const getUserInvite = async (
  status_invite: string = "enviado",
  type_invite: string = ""
): Promise<UserInviteResponse[]> => {
  try {
    const queryParams = new URLSearchParams();

    if (status_invite?.trim()) {
      queryParams.append("status_invite", status_invite.trim());
    }
    if (type_invite?.trim()) {
      queryParams.append("type_invite", type_invite.trim());
    }

    const response = await REPORTS_CLIENT.get<UserInviteResponse[]>(
      `/get_user_invite?${queryParams.toString()}`
    );
    console.log("[/get_user_invite] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error getting user invites:", error);
    throw error;
  }
};

export const postAnswerInvite = async (
  invite_answer: InviteAnswer
): Promise<ApiKeyResponse> => {
  try {
    if (typeof invite_answer.accept_invite !== "boolean") {
      throw new Error("Accept invite decision is required");
    }
    if (!invite_answer.invite_id?.trim()) {
      throw new Error("Invite ID is required");
    }

    const payload = {
      accept_invite: invite_answer.accept_invite,
      invite_id: invite_answer.invite_id.trim(),
    };

    console.log("[/answer_invite] payload: ", payload);
    const response = await REPORTS_CLIENT.post<ApiKeyResponse>(
      `/answer_invite`,
      payload
    );
    console.log("[/answer_invite] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error answering invite:", error);
    throw error;
  }
};

export const cancelSentInvite = async (
  invite_id: string
): Promise<ApiKeyResponse> => {
  try {
    console.log("[/cancel_invite] invite_id: ", invite_id);
    if (!invite_id?.trim()) {
      throw new Error("Invite ID is required");
    }

    console.log("[/cancel_invite] invite_id: ", invite_id.trim());
    const response = await REPORTS_CLIENT.put<ApiKeyResponse>(
      `/cancel_invite/${encodeURIComponent(invite_id.trim())}`,
      {}
    );
    console.log("[/cancel_invite] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error canceling invite:", error);
    throw error;
  }
};

export const removeUserFromOrganization = async (
  invite_id: string
): Promise<ApiKeyResponse> => {
  try {
    console.log("[/remove_user_from_organization] invite_id: ", invite_id);
    if (!invite_id?.trim()) {
      throw new Error("Invite ID is required");
    }

    const response = await REPORTS_CLIENT.post<ApiKeyResponse>(
      `/leave_organization`,
      {
        invite_id: invite_id.trim(),
      }
    );
    console.log(
      "[/remove_user_from_organization] response.data: ",
      response.data
    );
    return response.data;
  } catch (error) {
    console.error("Error removing user from organization:", error);
    throw error;
  }
};

export const postLeaveOrganization = async (): Promise<ApiKeyResponse> => {
  try {
    console.log("[/leave_organization] Requesting self-leave from organization");
    const response = await REPORTS_CLIENT.post<ApiKeyResponse>(
      `/leave_organization`,
      {}
    );
    console.log("[/leave_organization] response.data: ", response.data);
    return response.data;
  } catch (error) {
    console.error("Error leaving organization:", error);
    throw error;
  }
};

export const postRequestPasswordReset = async (): Promise<{ message: string }> => {
  try {
    const response = await REPORTS_CLIENT.post<{ message: string }>(`/request-password-reset`, {});
    return response.data;
  } catch (error) {
    console.error("Error requesting password reset:", error);
    throw error;
  }
};

export const postDismissPasswordResetRequest = async (userId: string): Promise<{ message: string }> => {
  try {
    const response = await REPORTS_CLIENT.post<{ message: string }>(`/dismiss-password-reset-request/${userId}`, {});
    return response.data;
  } catch (error) {
    console.error("Error dismissing password reset request:", error);
    throw error;
  }
};

export const getPasswordResetRequests = async (): Promise<Array<{
  user_id: string;
  name: string;
  email: string;
  role: string;
  requested_at: string | null;
}>> => {
  try {
    const response = await REPORTS_CLIENT.get<Array<{
      user_id: string;
      name: string;
      email: string;
      role: string;
      requested_at: string | null;
    }>>(`/password-reset-requests`);
    return response.data;
  } catch (error) {
    console.error("Error fetching password reset requests:", error);
    throw error;
  }
};

export const getInviteDetails = async (
  invite_id: string
): Promise<UserInviteResponse | UserInviteResponse[]> => {
  try {
    console.log("[/get_invite_details] invite_id: ", invite_id);
    if (!invite_id?.trim()) {
      throw new Error("Invite ID is required");
    }

    const response = await REPORTS_CLIENT.get<
      UserInviteResponse[] | UserInviteResponse
    >(`/get_invite_details/${invite_id?.trim()}`);
    console.log("[/get_invite_details] response.data: ", response.data);

    // API retorna um array com um único elemento
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data[0];
    }

    return response.data;
  } catch (error) {
    console.error("Error getting invite details:", error);
    throw error;
  }
};

export const updateUseSnapLogo = async (
  print_snap_logo: boolean
): Promise<{ message: string }> => {
  try {
    console.log("[/print_snap_logo] print_snap_logo: ", print_snap_logo);
    const response = await REPORTS_CLIENT.patch(`/print_snap_logo`, {
      print_snap_logo,
    });
    return response.data as { message: string };
  } catch (error) {
    console.error("Error updating print snap logo:", error);
    throw error;
  }
};

export const getEula = async (): Promise<EulaResponse> => {
  try {
    const response = await REPORTS_CLIENT.get<EulaResponse>(`/get_eula`);
    return response.data;
  } catch (error) {
    console.error("Error getting eula:", error);
    throw error;
  }
};

export const acceptTerms = async (): Promise<{ message: string }> => {
  try {
    const response = await REPORTS_CLIENT.patch(`/accept_terms`, {});
    return response.data as { message: string };
  } catch (error) {
    console.error("Error accepting terms:", error);
    throw error;
  }
};


// ===== Organization Users (new separated endpoints) =====

export const getOrganizationUsers = async (
  filters: OrganizationUsersFilters = {}
): Promise<PaginatedResponse<OrganizationUserResponse>> => {
  const {
    search_filter = "",
    type_filter = "",
    status_filter = "",
    license_type_filter = "",
    order = "desc",
    column_order = "joined_at",
    limit = 10,
    page = 1,
  } = filters;
  try {
    const queryParams = new URLSearchParams();
    if (search_filter?.trim()) {
      queryParams.append("search_filter", search_filter.trim());
    }
    if (type_filter?.trim()) {
      queryParams.append("type_filter", type_filter.trim());
    }
    if (status_filter?.trim()) {
      queryParams.append("status_filter", status_filter.trim());
    }
    if (license_type_filter?.trim()) {
      queryParams.append("license_type_filter", license_type_filter.trim());
    }
    queryParams.append("order", order || "desc");
    queryParams.append("column_order", column_order || "joined_at");
    queryParams.append("limit", limit.toString());
    queryParams.append("page", page.toString());

    const response = await REPORTS_CLIENT.get<PaginatedResponse<OrganizationUserResponse>>(
      `/get_organization_users?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error getting organization users:", error);
    throw error;
  }
};

export const editOrganizationUser = async (
  user_id: string,
  report_types: string[],
  role: string,
  credits_monthly: number,
  type_license?: "recorrente" | "poc"
) => {
  try {
    const response = await REPORTS_CLIENT.put(
      `/update_organization_user/${user_id}`,
      { report_types, role, credits_monthly, type_license: type_license ?? "recorrente" }
    );
    return response.data;
  } catch (error) {
    console.error("Error editing organization user:", error);
    throw error;
  }
};

export const removeOrganizationUser = async (
  user_id: string
): Promise<{ message: string }> => {
  try {
    const response = await REPORTS_CLIENT.post<{ message: string }>(
      `/remove_organization_user/${user_id}`,
      {}
    );
    return response.data;
  } catch (error) {
    console.error("Error removing organization user:", error);
    throw error;
  }
};

export const updatePendingInvite = async (
  invite_id: string,
  data: InviteUpdateRequest
) => {
  try {
    const response = await REPORTS_CLIENT.put(
      `/update_pending_invite/${invite_id}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error updating pending invite:", error);
    throw error;
  }
};

// ===== Batch Invite =====

export interface BatchInviteItem {
  email_invited: string;
  type_invite: string;
  report_types: string[];
  credits_sent: number;
  type_license?: "recorrente" | "poc";
}

export interface BatchInvitePayload {
  invites: BatchInviteItem[];
}

export interface BatchInviteResultItem {
  email: string;
  status: "sent" | "error";
  reason?: string;
}

export interface BatchInviteResponse {
  results: BatchInviteResultItem[];
}

export const postBatchInvite = async (
  payload: BatchInvitePayload
): Promise<BatchInviteResponse> => {
  try {
    const response = await REPORTS_CLIENT.post<BatchInviteResponse>(
      `/invites/batch`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error sending batch invite:", error);
    throw error;
  }
};
